
import Vue from 'vue';
import '@/assets/css/helpdesk/helpdesk-theme.css';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {IHelpdeskEmail} from '@/store/modules/helpdeskInboxModule';
import {IHelpdeskSentEmail} from '@/store/modules/helpdeskSentModule';
import {IGmailAccount} from '@/store/modules/helpdeskAccountsModule';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import EmailHtmlFrame from '@/components/helpdesk/EmailHtmlFrame.vue';
import TicketCreateModal from '@/components/helpdesk/TicketCreateModal.vue';
import RichTextEditor from '@/components/helpdesk/RichTextEditor.vue';
import EmailAttachments from '@/components/helpdesk/EmailAttachments.vue';
import {visibleAttachments} from '@/services/helpdeskEmailAttachments';
import {Watch} from 'vue-property-decorator';
import {IEmailTemplate} from '@/store/modules/helpdeskEmailTemplatesModule';
import {IHelpdeskTicket} from '@/store/modules/helpdeskTicketsModule';
import HelpdeskTicketModal from '@/components/helpdesk/HelpdeskTicketModal.vue';
import SignatureSelector from '@/components/helpdesk/SignatureSelector.vue';
import {INBOX_EMAIL_QUERY} from '@/helpers/helpdeskTicketEmails';
import {deletePreference, getPreference, IInboxFilters, INBOX_FILTERS_KEY, savePreference} from '@/services/helpdeskPreferences';

const mod = namespace('helpdeskInboxModule');

@Component({name: 'HelpdeskInbox', components: {EmailHtmlFrame, EmailAttachments, PaginationBar, TicketCreateModal, RichTextEditor, HelpdeskTicketModal, SignatureSelector}})
export default class HelpdeskInbox extends Vue {
  @mod.Getter('emails')        private emails!: IHelpdeskEmail[];
  @mod.Getter('selectedEmail') private selectedEmail!: IHelpdeskEmail | null;
  @mod.Getter('currentPage')   private currentPage!: number;
  @mod.Getter('lastPage')      private lastPage!: number;
  @mod.Getter('total')         private total!: number;
  @mod.Getter('perPage')       private perPage!: number;

  get fullThread(): any[] {
    return this.$store.getters['helpdeskSentModule/fullThread'] || [];
  }

  private loading          = false;
  private syncing          = false;
  private filterStatuses: string[] = [];
  private currentPageLocal = 1;
  private searchText       = '';
  private searchTimer: any = null;
  private ticketModalOpen  = false;
  private subscribes: any = {};
  private replyOpen        = false;
  private replyBody        = '';
  private replySubject     = '';
  private replyCc: string[]  = [];
  private replyCco: string[] = [];
  private replyTo: string[]  = [];
  // null = automatic: on only when the reply goes to someone other than the original sender.
  private replyIncludeHistoryChoice: boolean | null = null;
  private showCc           = false;
  private showCco          = false;
  private replying            = false;
  private templatePickerOpen   = false;
  private linkTicketOpen       = false;
  private linkTicketSearch     = '';
  private linkTicketTimer: any = null;
  private linkingTicket        = false;
  private inboxTicketModalOpen = false;
  private inboxTicketModalId: number | null = null;
  private replySignatureBody   = '';

  // ── Forward selected email ─────────────────────────────
  private forwardOpen            = false;
  private forwarding             = false;
  private forwardToEmail         = '';
  private forwardCc: string[]    = [];
  private forwardBcc: string[]   = [];
  private forwardCcVisible       = false;
  private forwardBccVisible      = false;

  // ── Compose new email ──────────────────────────────────
  private composeOpen            = false;
  private composing              = false;
  private composeFromAccountId: number | null = null;
  private composeToEmail         = '';
  private composeSubject         = '';
  private composeCc: string[]    = [];
  private composeBcc: string[]   = [];
  private composeBody            = '';
  private composeSignatureBody   = '';
  private composeCcVisible       = false;
  private composeBccVisible      = false;

  get gmailAccounts(): IGmailAccount[] {
    return this.$store.getters['helpdeskAccountsModule/accounts'] || [];
  }

  get composeFromAccountEmail(): string {
    if (!this.composeFromAccountId) return '';
    const acc = this.gmailAccounts.find((a: IGmailAccount) => a.id === this.composeFromAccountId);
    return acc ? acc.email : '';
  }

  get linkTicketResults(): IHelpdeskTicket[] {
    return this.$store.getters['helpdeskTicketsModule/tickets'] || [];
  }

  get allTemplates(): IEmailTemplate[] {
    return this.$store.getters['helpdeskEmailTemplatesModule/allTemplates'] || [];
  }

  private avatarColors = ['#1565C0','#2E7D32','#4527A0','#AD1457','#00695C','#5D4037','#37474F','#6A1B9A'];

  // Phones: one panel at a time (list or email), like a mail app. Desktop is untouched.
  private mobileShowDetail = false;

  get isMobile(): boolean {
    return this.$vuetify.breakpoint.width < 768;
  }

  private othersMenuOpen   = false;
  private accountMenuOpen  = false;
  private filterAccountId: number | null = null;

  private primaryFilterOptions = [
    {value: 'pending',         label: 'Pendiente', color: 'amber darken-4'},
    {value: 'ticket_assigned', label: 'Ticket',    color: 'indigo'},
    {value: 'ticket_resolved', label: 'Resuelto',  color: 'green darken-2'},
  ];

  get secondaryFilterOptions() {
    return [
      {value: 'read',     label: 'Leído',    color: 'blue-grey'},
      {value: 'spam',     label: 'Spam',     color: 'red darken-2'},
      {value: 'ignored',  label: 'Ignorado', color: 'grey darken-1'},
      {value: 'archived', label: this.$t('helpdesk.inbox.archived') as string, color: 'brown'},
    ];
  }

  get hasSecondaryFilter(): boolean {
    return this.secondaryFilterOptions.some(f => this.filterStatuses.indexOf(f.value) !== -1);
  }

  // ── Saved default filters (per user, kept by the helpdesk) ──
  private defaultsMenuOpen = false;
  private savingDefaults   = false;
  private savedFilters: IInboxFilters | null = null;
  // The store may still hold another view's page of accounts until this view's fetch answers.
  private accountsLoaded   = false;

  get isShowingDefaults(): boolean {
    if (!this.savedFilters) return false;
    const saved = this.savedFilters.statuses || [];
    return saved.length === this.filterStatuses.length
      && saved.every(s => this.filterStatuses.indexOf(s) !== -1)
      && (this.savedFilters.account_id || null) === this.filterAccountId;
  }

  get filterAccountLabel(): string {
    if (!this.filterAccountId) return 'Cuenta';
    const acc = this.gmailAccounts.find((a: IGmailAccount) => a.id === this.filterAccountId);
    // The whole address: all mailboxes share the domain, so "@smartpoint.ai" alone says nothing.
    return acc ? acc.email : 'Cuenta';
  }

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.emailsLoaded':       () => { this.loading = false; },
      'backend.call.KO.emailsLoaded':       () => { this.loading = false; },
      'backend.call.OK.emailStatusUpdated': () => { this.load(); },
      'backend.call.OK.syncDone':           () => { this.syncing = false; this.load(); },
      'backend.call.KO.syncDone':           () => { this.syncing = false; },
      'backend.call.OK.accountsLoaded':     () => { this.accountsLoaded = true; this.dropMissingAccountFilter(); },
    });
    this.$store.dispatch('helpdeskAccountsModule/fetchAccounts', {per_page: 100});
    this.loadSavedFilters();
  }

  beforeDestroy() { this.$offs(this.subscribes); }

  private readonly defaultStatuses = ['pending', 'read', 'ticket_assigned', 'ticket_resolved'];

  private load() {
    this.loading = true;
    const params: any = {page: this.currentPageLocal, per_page: this.perPage};
    params.statuses = this.filterStatuses.length ? this.filterStatuses : this.defaultStatuses;
    if (this.searchText)      params.search     = this.searchText;
    if (this.filterAccountId) params.account_id = this.filterAccountId;
    this.$store.dispatch('helpdeskInboxModule/fetchEmails', params);
  }

  private toggleFilter(value: string) {
    const idx = this.filterStatuses.indexOf(value);
    if (idx === -1) {
      this.filterStatuses = [...this.filterStatuses, value];
    } else {
      this.filterStatuses = this.filterStatuses.filter(s => s !== value);
    }
    this.currentPageLocal = 1;
    this.load();
  }

  private setAllFilter() {
    this.filterStatuses   = [];
    this.currentPageLocal = 1;
    this.load();
  }

  private setAccountFilter(id: number | null) {
    this.filterAccountId  = id;
    this.currentPageLocal = 1;
    this.load();
  }

  // The first page waits for the saved filters, so the inbox never flashes "Todos" first.
  private loadSavedFilters() {
    this.loading = true;
    getPreference<IInboxFilters>(INBOX_FILTERS_KEY)
      .then((saved) => {
        this.savedFilters = saved;
        if (saved) this.applyFilters(saved);
      })
      // Without them the inbox still opens, just with every email.
      .catch(() => { this.savedFilters = null; })
      .then(() => this.load());
  }

  private applyFilters(filters: IInboxFilters) {
    this.filterStatuses  = (filters.statuses || []).slice();
    this.filterAccountId = this.isMissingAccount(filters.account_id) ? null : (filters.account_id || null);
  }

  private applyDefaults() {
    if (!this.savedFilters) return;
    this.applyFilters(this.savedFilters);
    this.currentPageLocal = 1;
    this.load();
  }

  private saveDefaults() {
    const value: IInboxFilters = {statuses: this.filterStatuses.slice(), account_id: this.filterAccountId};
    this.savingDefaults = true;
    savePreference<IInboxFilters>(INBOX_FILTERS_KEY, value)
      .then((saved) => {
        this.savedFilters = saved || value;
        this.$uiBus.emit('success', this.$t('helpdesk.inbox.defaults.saved'));
      })
      .catch(() => this.$uiBus.emit('alert', this.$t('helpdesk.inbox.defaults.saveError')))
      .then(() => { this.savingDefaults = false; });
  }

  private clearDefaults() {
    this.savingDefaults = true;
    deletePreference(INBOX_FILTERS_KEY)
      .then(() => {
        this.savedFilters = null;
        this.$uiBus.emit('success', this.$t('helpdesk.inbox.defaults.cleared'));
      })
      .catch(() => this.$uiBus.emit('alert', this.$t('helpdesk.inbox.defaults.saveError')))
      .then(() => { this.savingDefaults = false; });
  }

  // A saved account that was deleted afterwards would leave the inbox empty with no visible reason.
  private isMissingAccount(id: number | null): boolean {
    return !!id && this.accountsLoaded && !this.gmailAccounts.some((a: IGmailAccount) => a.id === id);
  }

  private dropMissingAccountFilter() {
    if (this.isMissingAccount(this.filterAccountId)) this.setAccountFilter(null);
  }

  private onSync() {
    if (this.syncing) return;
    this.syncing = true;
    this.$store.dispatch('helpdeskAccountsModule/syncEmails');
  }

  private onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.currentPageLocal = 1;
      this.load();
    }, 400);
  }

  private onSearchClear() {
    this.searchText = '';
    this.currentPageLocal = 1;
    this.load();
  }

  private onPageChange(page: number) {
    this.currentPageLocal = page;
    this.load();
  }

  private hasAttachments(email: IHelpdeskEmail): boolean {
    return visibleAttachments(email.attachments).length > 0;
  }

  private openEmail(id: number) {
    if (this.isMobile) this.mobileShowDetail = true;
    this.$store.dispatch('helpdeskInboxModule/fetchEmail', id);
    this.$store.dispatch('helpdeskSentModule/fetchFullThread', id);
  }

  // "Ir al email" from a ticket: the email may be outside the current filters, it opens anyway.
  @Watch('$route.query.' + INBOX_EMAIL_QUERY, {immediate: true})
  private onEmailQueryChanged(value: string | undefined) {
    const id = Number(value);
    if (id) this.openEmail(id);
  }

  private onSelectEmail(email: IHelpdeskEmail) {
    this.openEmail(email.id);
    if (email.status === 'pending') {
      this.$store.dispatch('helpdeskInboxModule/markAsRead', email.id);
    }
  }

  private setStatus(status: string) {
    if (!this.selectedEmail) return;
    this.$store.dispatch('helpdeskInboxModule/updateEmailStatus', {
      id: this.selectedEmail.id,
      status,
    });
  }

  private unarchive() {
    if (!this.selectedEmail) return;
    this.$store.dispatch('helpdeskInboxModule/unarchiveEmail', this.selectedEmail.id);
  }

  private openTicketModal() { this.ticketModalOpen = true; }

  private openInboxTicketModal() {
    if (!this.selectedEmail || !this.selectedEmail.ticket_id) return;
    this.inboxTicketModalId   = this.selectedEmail.ticket_id;
    this.inboxTicketModalOpen = true;
  }

  private onInboxTicketSaved() {
    this.load();
    if (this.selectedEmail) {
      this.$store.dispatch('helpdeskInboxModule/fetchEmail', this.selectedEmail.id);
    }
  }

  private onInboxTicketDeleted() {
    this.inboxTicketModalOpen = false;
    this.load();
    if (this.selectedEmail) {
      this.$store.dispatch('helpdeskInboxModule/fetchEmail', this.selectedEmail.id);
    }
  }

  private onTicketCreated() {
    this.load();
    if (this.selectedEmail) {
      this.$store.dispatch('helpdeskInboxModule/fetchEmail', this.selectedEmail.id);
    }
  }

  // ── Respuesta ──────────────────────────────────────────
  // Keyed on the id: the same email is re-fetched after status changes, ticket creation or a reply,
  // and that must not wipe a reply the agent is still writing.
  get selectedEmailId(): number | null {
    return this.selectedEmail ? this.selectedEmail.id : null;
  }

  @Watch('selectedEmailId')
  private onEmailChanged() {
    this.replyOpen          = false;
    this.replyBody          = '';
    this.replySubject       = '';
    this.replyCc            = [];
    this.replyCco           = [];
    this.replyTo            = [];
    this.replyIncludeHistoryChoice = null;
    this.showCc             = false;
    this.showCco            = false;
    this.replying           = false;
    this.replySignatureBody = '';
    this.forwardOpen        = false;
    this.forwarding         = false;
    this.forwardToEmail     = '';
    this.forwardCc          = [];
    this.forwardBcc         = [];
    this.forwardCcVisible   = false;
    this.forwardBccVisible  = false;
  }

  private openLinkTicket() {
    this.linkTicketSearch = '';
    this.linkTicketOpen   = true;
    this.$store.dispatch('helpdeskTicketsModule/fetchTickets', {per_page: 20});
  }

  private onLinkTicketSearch() {
    clearTimeout(this.linkTicketTimer);
    this.linkTicketTimer = setTimeout(() => {
      this.$store.dispatch('helpdeskTicketsModule/fetchTickets', {
        per_page: 20,
        search:   this.linkTicketSearch,
      });
    }, 400);
  }

  private onLinkTicketClear() {
    this.linkTicketSearch = '';
    this.$store.dispatch('helpdeskTicketsModule/fetchTickets', {per_page: 20});
  }

  private doLinkTicket(ticket: IHelpdeskTicket) {
    if (!this.selectedEmail || this.linkingTicket) return;
    this.linkingTicket = true;
    const emailId  = this.selectedEmail.id;
    const ticketId = ticket.id;
    const unsub = (this as any).$ons({
      'backend.call.OK.emailLinkedToTicket': () => {
        this.linkingTicket  = false;
        this.linkTicketOpen = false;
        (this as any).$offs(unsub);
      },
      'backend.call.KO.emailLinkedToTicket': () => {
        this.linkingTicket = false;
        (this as any).$offs(unsub);
      },
    });
    this.$store.dispatch('helpdeskInboxModule/linkEmailToTicket', {emailId, ticketId});
  }

  private openTemplatePicker() {
    this.$store.dispatch('helpdeskEmailTemplatesModule/fetchAllTemplates');
    this.templatePickerOpen = true;
  }

  private applyTemplate(tpl: IEmailTemplate) {
    this.templatePickerOpen = false;
    this.openReply();
    this.replyBody = tpl.body;
    if (tpl.subject) {
      this.replySubject = tpl.subject;
    }
  }

  // Toolbar icons (Gmail-style): icon + descriptive tooltip instead of text labels.
  get replyActions(): Array<{key: string; icon: string; label: string; run: () => void}> {
    return [
      {key: 'reply',    icon: 'mdi-reply',           label: 'Responder',               run: () => this.replyFromToolbar()},
      {key: 'template', icon: 'mdi-text-box-outline', label: 'Responder con plantilla', run: () => this.openTemplatePicker()},
      {key: 'forward',  icon: 'mdi-share-outline',    label: 'Reenviar',                run: () => this.openForward()},
    ];
  }

  private readonly statusActions = [
    {status: 'pending', icon: 'mdi-clock-outline',          label: 'Marcar como pendiente', activeLabel: 'Pendiente (estado actual)'},
    {status: 'spam',    icon: 'mdi-alert-octagon-outline',  label: 'Marcar como spam',      activeLabel: 'Spam (estado actual)'},
    {status: 'ignored', icon: 'mdi-eye-off-outline',        label: 'Ignorar',               activeLabel: 'Ignorado (estado actual)'},
  ];

  // The reply editor sits below the thread: bring it into view and focus it.
  private replyFromToolbar() {
    if (!this.replyOpen) this.openReply();
    this.$nextTick(() => {
      const el = this.$refs.replySection as HTMLElement | undefined;
      if (!el) return;
      el.scrollIntoView({behavior: 'smooth', block: 'start'});
      const editable = el.querySelector('[contenteditable="true"]') as HTMLElement | null;
      if (editable) editable.focus();
    });
  }

  private openReply() {
    this.replyOpen    = true;
    this.replyBody    = '';
    this.replySubject = this.selectedEmail
      ? (this.selectedEmail.subject.startsWith('Re:') ? this.selectedEmail.subject : 'Re: ' + this.selectedEmail.subject)
      : '';
    this.replyCc  = [];
    this.replyCco = [];
    this.replyTo  = this.selectedEmail ? [this.selectedEmail.from_email] : [];
    this.replyIncludeHistoryChoice = null;
    this.showCc   = false;
    this.showCco  = false;
  }

  // Addresses already in the thread: the sender and anyone copied on our previous replies.
  get replyToSuggestions(): string[] {
    const found: string[] = this.selectedEmail ? [this.selectedEmail.from_email] : [];
    this.fullThread.forEach((item: any) => {
      // to_email may hold several recipients joined by commas.
      const to = (item.to_email || '').split(',');
      [item.from_email, ...to, ...(item.cc_emails || [])].forEach((raw: string) => {
        const addr = (raw || '').trim();
        if (addr && !found.includes(addr)) found.push(addr);
      });
    });
    return found;
  }

  get replyToInvalid(): string[] {
    return this.replyTo.filter(addr => !/.+@.+\..+/.test(addr));
  }

  // The backend stores the recipients joined by ", " in a varchar(255).
  get replyToTooLong(): boolean {
    return this.replyTo.join(', ').length > 255;
  }

  get replyGoesToSender(): boolean {
    return !!this.selectedEmail && this.replyTo.length === 1 && this.replyTo[0] === this.selectedEmail.from_email;
  }

  get replyIncludeHistory(): boolean {
    return this.replyIncludeHistoryChoice !== null ? this.replyIncludeHistoryChoice : !this.replyGoesToSender;
  }

  set replyIncludeHistory(value: boolean) {
    this.replyIncludeHistoryChoice = value;
  }

  get canSendReply(): boolean {
    return !!this.replyBody && !this.replying && this.replyTo.length > 0 && !this.replyToInvalid.length && !this.replyToTooLong;
  }

  private closeReply() {
    this.replyOpen          = false;
    this.replyBody          = '';
    this.replySubject       = '';
    this.replyCc            = [];
    this.replyCco           = [];
    this.replyTo            = [];
    this.replyIncludeHistoryChoice = null;
    this.showCc             = false;
    this.showCco            = false;
    this.replySignatureBody = '';
  }

  get reporterEmail(): string {
    const userData = this.$store.getters['authModule/userData'];
    return (userData && userData.email) ? userData.email : '';
  }

  private sendReply() {
    if (!this.selectedEmail || !this.canSendReply) return;
    this.replying = true;
    const emailId = this.selectedEmail.id;
    const finalBody = this.replySignatureBody
      ? this.replyBody + '<br><br>' + this.replySignatureBody
      : this.replyBody;
    this.$store.dispatch('helpdeskInboxModule/replyEmail', {
      id:      emailId,
      body:    finalBody,
      sent_by: this.reporterEmail || undefined,
      subject: this.replySubject || undefined,
      // Only sent when changed, so a plain reply keeps the backend default (the sender).
      to:      this.replyGoesToSender ? undefined : this.replyTo,
      include_history: this.replyIncludeHistory || undefined,
      cc:      this.replyCc.length  ? this.replyCc  : undefined,
      bcc:     this.replyCco.length ? this.replyCco : undefined,
    });
    const unsub = (this as any).$ons({
      'backend.call.OK.emailReplied': () => {
        this.replying     = false;
        this.replyOpen    = false;
        this.replyBody    = '';
        this.replySubject = '';
        // Refresh only if the agent is still on that email; otherwise it would jump back to it.
        if (this.selectedEmailId === emailId) {
          this.$store.dispatch('helpdeskSentModule/fetchFullThread', emailId);
          this.$store.dispatch('helpdeskInboxModule/fetchEmail', emailId);
        }
        (this as any).$offs(unsub);
      },
      'backend.call.KO.emailReplied': () => {
        this.replying = false;
        (this as any).$offs(unsub);
      },
    });
  }

  // ── Reenviar email seleccionado ───────────────────────
  private openForward() {
    if (!this.selectedEmail) return;
    this.forwardToEmail    = '';
    this.forwardCc         = [];
    this.forwardBcc        = [];
    this.forwardCcVisible  = false;
    this.forwardBccVisible = false;
    this.forwarding        = false;
    this.forwardOpen       = true;
  }

  private closeForward() {
    this.forwardOpen       = false;
    this.forwardToEmail    = '';
    this.forwardCc         = [];
    this.forwardBcc        = [];
    this.forwardCcVisible  = false;
    this.forwardBccVisible = false;
  }

  private sendForward() {
    if (!this.selectedEmail || !this.forwardToEmail || this.forwarding) return;
    this.forwarding = true;
    const emailId = this.selectedEmail.id;
    this.$store.dispatch('helpdeskInboxModule/forwardEmail', {
      id:       emailId,
      to_email: this.forwardToEmail,
      sent_by:  this.reporterEmail || undefined,
      cc:       this.forwardCc.length  ? this.forwardCc  : undefined,
      bcc:      this.forwardBcc.length ? this.forwardBcc : undefined,
    });
    const unsub = (this as any).$ons({
      'backend.call.OK.emailForwarded': () => {
        this.forwarding = false;
        this.closeForward();
        if (this.selectedEmailId === emailId) {
          this.$store.dispatch('helpdeskSentModule/fetchFullThread', emailId);
        }
        (this as any).$offs(unsub);
      },
      'backend.call.KO.emailForwarded': () => {
        this.forwarding = false;
        (this as any).$offs(unsub);
      },
    });
  }

  // ── Redactar nuevo correo ─────────────────────────────
  private openCompose() {
    this.$store.dispatch('helpdeskAccountsModule/fetchAccounts', {per_page: 100});
    this.composeFromAccountId  = null;
    this.composeToEmail        = '';
    this.composeSubject        = '';
    this.composeCc             = [];
    this.composeBcc            = [];
    this.composeBody           = '';
    this.composeSignatureBody  = '';
    this.composeCcVisible      = false;
    this.composeBccVisible     = false;
    this.composing             = false;
    this.composeOpen           = true;
  }

  private sendCompose() {
    if (!this.composeToEmail || !this.composeSubject || !this.composeBody) return;
    this.composing = true;
    const data: any = {
      to_email:       this.composeToEmail,
      subject:        this.composeSubject,
      body:           this.composeBody,
      sent_by:        this.reporterEmail || undefined,
      signature_body: this.composeSignatureBody || undefined,
    };
    if (this.composeFromAccountId)  data.account_id = this.composeFromAccountId;
    if (this.composeCc.length)      data.cc          = this.composeCc;
    if (this.composeBcc.length)     data.bcc         = this.composeBcc;
    this.$store.dispatch('helpdeskSentModule/composeEmail', data);
    const unsub = (this as any).$ons({
      'backend.call.OK.emailComposed': () => {
        this.composing    = false;
        this.composeOpen  = false;
        (this as any).$offs(unsub);
      },
      'backend.call.KO.emailComposed': () => {
        this.composing = false;
        (this as any).$offs(unsub);
      },
    });
  }

  // ── Helpers visuales ──────────────────────────────────
  private statusLabel(status: string): string {
    const map: {[k: string]: string} = {
      pending:         'Pendiente',
      read:            'Leído',
      spam:            'Spam',
      ignored:         'Ignorado',
      archived:        this.$t('helpdesk.inbox.archived') as string,
      ticket_assigned: 'Ticket',
      ticket_resolved: 'Resuelto',
    };
    return map[status] || status;
  }


  private statusClass(status: string): string {
    return status === 'pending' ? 'inbox-email-item--unread' : '';
  }

  private initials(email: string) {
    return email ? email.charAt(0).toUpperCase() : '?';
  }

  private avatarColor(email: string) {
    const idx = email.charCodeAt(0) % this.avatarColors.length;
    return this.avatarColors[idx];
  }

  private shortDate(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: '2-digit'}) +
      ' ' + d.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }

  private formatDate(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('es-ES', {day: 'numeric', month: 'short', year: 'numeric'}) +
      ' ' + d.toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }
}
